import { useState } from 'react';
import { Alert, Button, Checkbox, StatusHint } from '@indipia/ui-core';
import type { CustReqItem } from '@indipia/shared-features';
import { PhotoLightbox } from '../PhotoLightbox';
import { ThumbImg } from '../ThumbImg';
import { CUST_REQ_ORDER_MESSAGE as M, reqMetaText } from '../../utils/custReqOrder.logic';

/**
 * 공종 전달 카드 — 작업요청에 함께 보낼 고객 요청 체크 목록(941 S16-6·S16-8).
 * 기본은 전부 켜짐, 해제 = 이번에 빼기(접수로 남음). 원문은 여기서 고칠 수 없다.
 * 요청 목록은 호출하는 쪽이 조회해서 넘긴다(단건 작성·일괄 작성 공용). 목록이 비면 아무것도 그리지 않는다.
 */
export type CustReqLoadStatus = 'loading' | 'error' | 'ready';

interface Props {
  /** 접수 상태 요청(발송 대상 후보) */
  items: CustReqItem[];
  /** 이번에 빼기로 체크 해제한 요청 ID */
  excluded: ReadonlySet<number>;
  onToggle: (custReqId: number) => void;
  status: CustReqLoadStatus;
  onRetry: () => void;
  className?: string;
}

export function CustReqDeliveryCard({ items, excluded, onToggle, status, onRetry, className }: Props) {
  const [lightbox, setLightbox] = useState<{ item: number; photo: number } | null>(null);

  if (status === 'loading' && items.length === 0) {
    return <div className={className}><StatusHint tone="muted">{M.loading}</StatusHint></div>;
  }
  if (status === 'error') {
    return (
      <div className={className}>
        <Alert
          kind="error"
          role="status"
          action={<Button type="button" variant="etc" size="sm" onClick={onRetry}>{M.retry}</Button>}
        >
          <span>{M.loadError}</span>
        </Alert>
      </div>
    );
  }
  if (items.length === 0) return null;

  const lbItem = lightbox ? items[lightbox.item] : null;

  return (
    <section aria-label="고객 요청" className={`rounded-lg border border-border bg-surface p-04 ${className ?? ''}`}>
      <div className="flex flex-wrap items-baseline gap-sm">
        <span className="text-body-sm font-bold text-text">{M.cardTitle(items.length)}</span>
        <span className="text-caption text-text-muted">{M.cardHint}</span>
      </div>
      <ul className="mt-03 flex flex-col gap-03">
        {items.map((it, i) => {
          const off = excluded.has(it.custReqId);
          return (
            <li key={it.custReqId} className="list-none">
              <div className="flex items-start gap-03">
                <span className="flex h-6 shrink-0 items-center">
                  <Checkbox
                    bare
                    checked={!off}
                    onChange={() => onToggle(it.custReqId)}
                    aria-label={`고객 요청 ${i + 1} 함께 전달`}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-body-sm leading-relaxed ${off ? 'text-text-muted' : 'text-text'}`}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {it.content}
                  </p>
                  <div className="mt-01 text-caption text-text-muted">{reqMetaText(it.srcName, it.receivedAt, it.photos.length)}</div>
                  {off && <StatusHint tone="muted" className="mt-01">{M.excluded}</StatusHint>}
                  {it.photos.length > 0 && (
                    <div className="mt-02 flex flex-wrap gap-sm">
                      {it.photos.map((p, pi) => (
                        <button
                          key={p.photoId}
                          type="button"
                          aria-label={`고객 요청 ${i + 1} 사진 ${pi + 1} 크게보기`}
                          onClick={() => setLightbox({ item: i, photo: pi })}
                          className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-surface-muted p-0"
                        >
                          <ThumbImg url={p.thumbUrl} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* 크게보기 — 인증이 필요한 원본이라 blob 로더(ThumbImg)로 렌더 */}
      <PhotoLightbox
        photos={(lbItem?.photos ?? []).map((p) => ({ url: p.url }))}
        index={lightbox && lbItem ? lightbox.photo : null}
        onClose={() => setLightbox(null)}
        onIndexChange={(pi) => setLightbox((cur) => (cur ? { item: cur.item, photo: pi } : cur))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />
    </section>
  );
}
